<template>
  <div v-if="authIsReady">
    <UnifiedHeader />
    <main class="space-y-8">
      <FloatingAiButton />
      <!-- START: This section is correctly hidden from Standard Users -->
      <div v-if="isClientAdmin">
        <UserManagementPanel 
          :users="standardUsers" 
          :loading="usersLoading" 
          @users-changed="fetchStandardUsers" 
        />
      </div>
      <!-- END: This section is correctly hidden from Standard Users -->
      <div class="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start mt-8">
        <div class="lg:col-span-2 space-y-8">
          <ProfilePanel />
          <CalendarPanel :submissions="submissions" />
          <ProductionChecklist />
          <RssFeedDisplayPanel />
        </div>
        <div class="lg:col-span-3 space-y-8">
          <!-- START: DEFINITIVE FIX - Wrap admin panels in v-if -->
          <div v-if="isClientAdmin">
            <PodcastProfilePanel :client-data="clientData" @profile-updated="fetchClientData" class="mb-8" />
            <EpisodeManagementPanel />
          </div>
          <!-- END: DEFINITIVE FIX - Wrap admin panels in v-if -->
          
          <div v-if="clientData" class="bg-white shadow-md rounded-xl p-6">
            <h2 class="text-xl font-semibold text-gray-800 border-b pb-4">Your Public Page</h2>
            <div class="mt-4">
              <p class="text-sm text-gray-600 mb-3">This is the public-facing URL for your podcast. Share this link with your audience.</p>
              <router-link :to="publicPageUrl" target="_blank" class="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-brand-purple-dark hover:opacity-90">
                View Your Public Podcast Page
                <svg class="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path></svg>
              </router-link>
            </div>
          </div>
          <InviteGuestPanel />
          <GuestManagementPanel />
          <!-- EpisodeManagementPanel has been moved up into the v-if block -->
        </div>
      </div>
    </main>
    <footer class="mt-12 text-center py-4 space-y-4">
      <div class="flex items-center justify-center space-x-2"><img src="@/assets/logo.png" alt="Castive Logo" class="h-6 w-auto opacity-75"><p class="text-sm text-gray-600 font-medium">Powered by Castive</p></div>
      <div class="md:hidden"><a href="https://mannmadecreations.co.uk" target="_blank" rel="noopener noreferrer" class="pixel-perfect-link">GET PIXEL PERFECTION</a></div>
    </footer>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { db } from '@/firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import UnifiedHeader from '@/components/UnifiedHeader.vue';
import GuestManagementPanel from '@/components/GuestManagementPanel.vue';
import ProductionChecklist from '@/components/ProductionChecklist.vue';
import InviteGuestPanel from '@/components/InviteGuestPanel.vue';
import UserManagementPanel from '@/components/UserManagementPanel.vue';
import EpisodeManagementPanel from '@/components/EpisodeManagementPanel.vue';
import CalendarPanel from '@/components/CalendarPanel.vue';
import ProfilePanel from '@/components/ProfilePanel.vue';
import RssFeedDisplayPanel from '@/components/RssFeedDisplayPanel.vue';
import FloatingAiButton from '@/components/FloatingAiButton.vue';
import PodcastProfilePanel from '@/components/PodcastProfilePanel.vue';

export default {
  name: 'ClientDashboard',
  components: {
    UnifiedHeader,
    GuestManagementPanel, ProductionChecklist, InviteGuestPanel, UserManagementPanel,
    EpisodeManagementPanel, CalendarPanel, ProfilePanel, RssFeedDisplayPanel,
    FloatingAiButton,
    PodcastProfilePanel,
  },
  data() {
    return {
      clientData: null,
      standardUsers: [],
      usersLoading: true,
      submissions: [],
    }
  },
  computed: { 
    ...mapState(['authIsReady', 'clientId']),
    ...mapGetters(['isClientAdmin']),
    publicPageUrl() {
      return this.clientData?.slug ? `/shows/${this.clientData.slug}` : '/';
    }
  },
  methods: {
    async fetchClientData() {
      if (!this.clientId) return;
      const clientRef = doc(db, 'clients', this.clientId);
      const clientSnap = await getDoc(clientRef);
      if (clientSnap.exists()) {
        this.clientData = clientSnap.data();
      }
    },
    async fetchStandardUsers() {
      if (!this.clientId || !this.isClientAdmin) {
        this.usersLoading = false;
        return;
      }
      this.usersLoading = true;
      try {
        const functions = getFunctions(undefined, 'europe-west2');
        const getStandardUsers = httpsCallable(functions, 'getStandardUsers');
        const response = await getStandardUsers();
        this.standardUsers = response.data || [];
      } catch (error) {
        console.error("Error fetching standard users:", error);
        this.standardUsers = [];
      } finally {
        this.usersLoading = false;
      }
    },
    async fetchSubmissions() {
      if (!this.clientId) return;
      try {
        const submissionsRef = collection(db, 'submissions');
        const q = query(submissionsRef, where('clientId', '==', this.clientId));
        const querySnapshot = await getDocs(q);
        this.submissions = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      } catch (error) {
        console.error("Error fetching submissions for calendar:", error);
        this.submissions = [];
      }
    },
    fetchAllData() {
      this.fetchClientData();
      this.fetchStandardUsers();
      this.fetchSubmissions();
    }
  },
  created() {
    if (this.authIsReady) {
      this.fetchAllData();
    }
  },
  watch: {
    authIsReady(isReady) {
      if (isReady) {
        this.fetchAllData();
      }
    }
  }
}
</script>